import { useState } from "react"
import AutoGrowTextarea from "@/components/AutoGrowTextarea/AutoGrowTextarea"
import Button from "@/components/Button/Button"
import JobStatement from "@/components/JobStatement/JobStatement"
import Modal from "@/components/Modal/Modal"
import { JOB_COPY } from "@/content/job"
import { useFirestoreSubmit } from "@/hooks/useFirestoreWrite"
import { updateJob } from "@/services/jobs"
import { jobHolds } from "@/store/selectors"
import type { Job, JobInput } from "@/types"
import jobStyles from "@/styles/pages/Job.module.css"
import styles from "@/styles/components/JobEditModal.module.css"

/*
 * Fix a locked job from a later round — a typo or a word, without stepping the
 * whole room back to the Job screen.
 *
 * The same layout as Name-the-job (reusing `Job.module.css`'s field classes so the
 * two cannot drift), but a different write bargain: the Job screen auto-saves each
 * keystroke because the room is watching it appear, whereas this holds a LOCAL
 * draft and commits it as ONE `updateJob` on Save — the room sees the edit only
 * once it lands, and Cancel throws it away. Save is refused until the sentence
 * still holds, so an edit cannot blank a field the later rounds depend on.
 *
 * Facilitator-only, and only while the session is live: `updateJob` is gated by
 * `drivesSession && sessionIsLive` in `firestore.rules`, which is why the opener
 * (`JobStatement`'s Edit button) is hidden on a completed report rather than
 * offering a write the rules would refuse.
 */

interface JobEditModalProps {
  sessionId: string
  job: Job
  userStoryEnabled: boolean
  onClose: () => void
}

export default function JobEditModal({
  sessionId,
  job,
  userStoryEnabled,
  onClose,
}: JobEditModalProps) {
  const [who, setWho] = useState(job.who)
  const [what, setWhat] = useState(job.what)
  const [why, setWhy] = useState(job.why)
  const { submit, pending, error } = useFirestoreSubmit<boolean>()

  // The live preview and the holds check read the draft, not the saved job.
  const draft: Job = { ...job, who, what, why }
  const holds = jobHolds(draft, userStoryEnabled)

  const save = async () => {
    const patch: JobInput = userStoryEnabled ? { who, what, why } : { what }
    const ok = await submit(async () => {
      await updateJob(sessionId, job.id, patch)
      return true
    })
    if (ok) onClose()
  }

  return (
    <Modal
      title={JOB_COPY.editTitle}
      description={JOB_COPY.editBlurb}
      width="narrow"
      dismissible
      onClose={onClose}
      actions={
        <>
          <Button variant="quiet" onClick={onClose}>
            {JOB_COPY.editCancel}
          </Button>
          {/* Disabled until the sentence holds — a saved blank would shut the deck
              the later rounds are built on. Spinner while the one write is out. */}
          <Button onClick={save} disabled={!holds} loading={pending}>
            {JOB_COPY.editSave}
          </Button>
        </>
      }
    >
      {error ? <p className={styles.error}>{error}</p> : null}
      <div className={styles.form}>
        <div className={jobStyles.fields}>
          {userStoryEnabled ? (
            <>
              <ModalField
                label={JOB_COPY.whoLabel}
                placeholder={JOB_COPY.whoPlaceholder}
                value={who}
                onChange={setWho}
              />
              <ModalField
                label={JOB_COPY.whatLabel}
                placeholder={JOB_COPY.whatPlaceholder}
                value={what}
                onChange={setWhat}
              />
              <ModalField
                label={JOB_COPY.whyLabel}
                placeholder={JOB_COPY.whyPlaceholder}
                value={why}
                onChange={setWhy}
              />
            </>
          ) : (
            <ModalField
              label={JOB_COPY.plainLabel}
              placeholder={JOB_COPY.plainPlaceholder}
              value={what}
              onChange={setWhat}
            />
          )}
        </div>
        {/* The same blue result note as the Job screen, previewing the draft. */}
        <JobStatement job={draft} userStoryEnabled={userStoryEnabled} />
      </div>
    </Modal>
  )
}

function ModalField({
  label,
  placeholder,
  value,
  onChange,
}: {
  label: string
  placeholder: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <label className={jobStyles.field}>
      <span className={jobStyles.fieldLabel}>{label}</span>
      <AutoGrowTextarea
        className={jobStyles.fieldInput}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  )
}
